import React, { useState } from "react";
import {
  Box,
  Chip,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  HOLIDAY_TYPES,
  HOLIDAY_NAME_PRESETS,
  detectHolidayType,
  PURPLE,
  PURPLE_SOFT,
} from "../ResourceAllocationShared";

function groupWeeksByMonth(weeks) {
  const groups = {};
  weeks.forEach((w) => {
    const [year, month] = w.start_date.split("-");
    const key = `${year}-${month}`;
    if (!groups[key]) {
      const d = new Date(`${year}-${month}-15`);
      const label = d.toLocaleString("en-GB", { month: "long", year: "numeric" });
      groups[key] = { key, label, weeks: [] };
    }
    groups[key].weeks.push(w);
  });
  return Object.values(groups).sort((a, b) => a.key.localeCompare(b.key));
}

function formatDateShort(dateStr) {
  const d = new Date(`${dateStr}T12:00:00`);
  return d.toLocaleString("en-GB", { month: "short", day: "numeric" });
}

function WeekCell({ week, draft, isSelected, onToggle, onSelectWeek, onPresetApply, onNameChange, hideWeekNumber = false }) {
  const [customMode, setCustomMode] = useState(false);
  const htype = draft.is_holiday ? HOLIDAY_TYPES[detectHolidayType(draft.holiday_name)] : null;
  const isDirty = draft.is_holiday !== week.is_holiday || draft.holiday_name !== (week.holiday_name || "");
  const isPreset = HOLIDAY_NAME_PRESETS.some((p) => p.name === draft.holiday_name);

  const handleSelectChange = (e) => {
    const val = e.target.value;
    if (val === "__custom__") {
      setCustomMode(true);
    } else {
      setCustomMode(false);
      const preset = HOLIDAY_NAME_PRESETS.find((p) => p.name === val);
      if (preset) onPresetApply(week.id, preset);
    }
  };

  const handleToggleChange = (e) => {
    e.stopPropagation();
    setCustomMode(false);
    onToggle(week.id, e.target.checked);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        flex: "1 1 130px",
        minWidth: 115,
        maxWidth: 195,
        borderRadius: 2,
        border: "1px solid",
        borderColor: isSelected ? PURPLE : (htype ? htype.border : "#eef2f7"),
        borderWidth: isSelected ? 2 : 1,
        bgcolor: isSelected ? PURPLE_SOFT : (htype ? htype.bg : "#fff"),
        position: "relative",
        overflow: "hidden",
        transition: "border-color 100ms ease, background-color 100ms ease",
        "&:hover": {
          borderColor: htype ? htype.color : PURPLE,
          boxShadow: "0 1px 6px 0 rgba(124,58,237,0.08)",
        },
      }}
    >
      {isDirty && (
        <Box sx={{
          position: "absolute", top: 5, right: 5,
          width: 6, height: 6, borderRadius: "50%", bgcolor: PURPLE,
          zIndex: 1,
        }} />
      )}

      {/* Header: checkbox | Week N (or date) | toggle */}
      <Stack direction="row" alignItems="center" sx={{ px: 1, pt: 0.75 }}>
        <Box
          onClick={(e) => { e.stopPropagation(); onSelectWeek(week.id); }}
          sx={{ flexShrink: 0, lineHeight: 0, cursor: "pointer" }}
        >
          {isSelected
            ? <CheckBoxIcon sx={{ fontSize: 16, color: PURPLE }} />
            : <CheckBoxOutlineBlankIcon sx={{ fontSize: 16, color: "#cbd5e1" }} />}
        </Box>
        <Typography variant="caption" fontWeight={700} sx={{ flex: 1, pl: 0.5 }}>
          {hideWeekNumber ? formatDateShort(week.start_date) : `Week ${week.week_number}`}
        </Typography>
        <Switch
          size="small"
          checked={!!draft.is_holiday}
          onClick={(e) => e.stopPropagation()}
          onChange={handleToggleChange}
          sx={{
            flexShrink: 0,
            "& .MuiSwitch-switchBase.Mui-checked": { color: PURPLE },
            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: PURPLE },
          }}
        />
      </Stack>

      {/* Date range */}
      <Typography variant="caption" sx={{ color: "#64748b", px: 1, display: "block", lineHeight: 1.4, pb: 0.5, fontSize: 10 }}>
        {formatDateShort(week.start_date)} – {formatDateShort(week.end_date)}
      </Typography>

      {/* Holiday name selector or Teaching chip */}
      <Box sx={{ px: 1, pb: 0.75 }}>
        {draft.is_holiday ? (
          <Box onClick={(e) => e.stopPropagation()}>
            <Select
              size="small"
              value={isPreset ? draft.holiday_name : "__custom__"}
              onChange={handleSelectChange}
              displayEmpty
              sx={{
                width: "100%",
                height: 24,
                fontSize: 10,
                fontWeight: 600,
                bgcolor: htype ? htype.bg : "#f1f5f9",
                color: htype ? htype.color : "#475569",
                borderRadius: "12px",
                "& .MuiOutlinedInput-notchedOutline": { border: `1px solid ${htype ? htype.border : "#cbd5e1"}` },
                "&:hover .MuiOutlinedInput-notchedOutline": { border: `1px solid ${htype ? htype.color : PURPLE}` },
                "& .MuiSelect-select": { py: 0, px: 1, display: "flex", alignItems: "center" },
                "& .MuiSvgIcon-root": { fontSize: 14, right: 4 },
              }}
            >
              {HOLIDAY_NAME_PRESETS.map((p) => (
                <MenuItem key={p.name} value={p.name} sx={{ fontSize: 12 }}>
                  {HOLIDAY_TYPES[p.type].icon} {p.name}
                </MenuItem>
              ))}
              <MenuItem value="__custom__" sx={{ fontSize: 12 }}>✏️ Custom name…</MenuItem>
            </Select>
            {customMode && (
              <TextField
                size="small"
                value={draft.holiday_name}
                onChange={(e) => onNameChange(week.id, e.target.value)}
                placeholder="Holiday name…"
                autoFocus
                sx={{
                  mt: 0.5,
                  width: "100%",
                  "& .MuiInputBase-input": { py: 0.5, fontSize: 10 },
                }}
              />
            )}
          </Box>
        ) : (
          <Chip
            size="small"
            label="Teaching"
            sx={{ bgcolor: "#f0fdf4", color: "#16a34a", fontWeight: 600, border: "1px solid #bbf7d0", height: 20, fontSize: 10 }}
          />
        )}
        {week.is_holiday && week.deadline_moved_to_week_number && (
          <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", fontSize: 9, mt: 0.25 }}>
            Deadline → W{week.deadline_moved_to_week_number}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

export default function HolidayCalendarView({
  weeks,
  drafts,
  onToggle,
  onPresetApply,
  onNameChange,
  onSelectWeek,
  selectedWeekIds,
  hideWeekNumber = false,
}) {
  const monthGroups = groupWeeksByMonth(weeks);

  return (
    <Stack spacing={2}>
      {monthGroups.map((group) => (
        <Box key={group.key}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: "#64748b", display: "block", mb: 0.75, textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            {group.label}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {group.weeks.map((w) => {
              const draft = drafts[w.id] ?? { is_holiday: w.is_holiday, holiday_name: w.holiday_name || "" };
              return (
                <WeekCell
                  key={w.id}
                  week={w}
                  draft={draft}
                  isSelected={selectedWeekIds.has(w.id)}
                  onToggle={onToggle}
                  onSelectWeek={onSelectWeek}
                  onPresetApply={onPresetApply}
                  onNameChange={onNameChange}
                  hideWeekNumber={hideWeekNumber}
                />
              );
            })}
          </Box>
        </Box>
      ))}
    </Stack>
  );
}
